<script lang="ts">
  import { goto } from '@mateothegreat/svelte5-router'
  import { Button } from '$lib/components/ui/button/index.js'
  import { currentPath } from '$lib/store'
  import favicon from '$lib/assets/icons/favicon.svg'
  import ArrowLeft from '@lucide/svelte/icons/arrow-left'
  import Home from '@lucide/svelte/icons/home'

  let { timestamp }: { timestamp?: string } = $props()

  const attempted = $derived($currentPath || '/')
</script>

<section
  class="not-found relative isolate mx-auto flex min-h-[calc(100dvh-5.5rem)] w-full max-w-3xl flex-col items-center justify-center overflow-hidden px-6 py-16 text-center"
  aria-labelledby="not-found-title"
>
  <div
    class="not-found__glow pointer-events-none absolute left-1/2 top-[38%] h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-green)_22%,transparent),transparent_68%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-green)_18%,transparent),transparent_68%)]"
    aria-hidden="true"
  ></div>
  <div
    class="not-found__glow-2 pointer-events-none absolute left-[18%] top-[22%] h-56 w-56 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-sky)_16%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-sky)_12%,transparent),transparent_70%)]"
    aria-hidden="true"
  ></div>
  <div
    class="not-found__glow-3 pointer-events-none absolute bottom-[18%] right-[12%] h-48 w-48 rounded-full bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-latte-peach)_14%,transparent),transparent_70%)] dark:bg-[radial-gradient(circle,color-mix(in_oklab,var(--color-macchiato-peach)_10%,transparent),transparent_70%)]"
    aria-hidden="true"
  ></div>

  <div class="not-found__stack relative z-[1] flex w-full flex-col items-center">

    <div class="not-found__numeral relative select-none" aria-hidden="true">
      <span
        class="not-found__four block font-semibold leading-none tracking-tighter text-latte-green/90 dark:text-macchiato-green/90"
        style="font-size: clamp(5.5rem, 22vw, 9.5rem);"
      >
        404
      </span>
      <svg
        class="not-found__bookmark absolute -right-1 top-1 h-11 w-11 text-latte-red dark:text-macchiato-red sm:-right-2 sm:top-2 sm:h-14 sm:w-14"
        viewBox="0 0 48 48"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          d="M14 5h20c1.1 0 2 .9 2 2v35.2c0 .7-.8 1.1-1.4.7L24 35.4 13.4 42.9c-.6.4-1.4 0-1.4-.7V7c0-1.1.9-2 2-2Z"
          opacity="0.92"
        />
      </svg>
    </div>

    <h1
      id="not-found-title"
      class="not-found__title mt-4 text-2xl font-semibold tracking-tight text-latte-text sm:text-3xl dark:text-macchiato-text"
    >
      This page slipped the shelf
    </h1>

    <p
      class="not-found__copy mt-3 max-w-md text-sm leading-relaxed text-latte-text/70 sm:text-base dark:text-macchiato-text/70"
    >
      Nothing lives at
      <code
        class="mx-0.5 rounded bg-latte-mantle/80 px-1.5 py-0.5 font-mono text-[0.85em] text-latte-green dark:bg-macchiato-mantle/80 dark:text-macchiato-green"
      >{attempted}</code
      >
      — maybe it moved, or never existed.
    </p>

    <div class="not-found__actions mt-9 flex flex-wrap items-center justify-center gap-3">
      <Button
        class="h-11 gap-2 bg-latte-green px-5 font-semibold text-latte-base hover:bg-latte-green/90 dark:bg-macchiato-green dark:text-macchiato-base dark:hover:bg-macchiato-green/90"
        onclick={() => goto('/')}
      >
        <Home class="size-4" strokeWidth={2} />
        Back to shelves
      </Button>
      <Button
        variant="outline"
        class="h-11 gap-2 border-latte-surface1/80 bg-latte-mantle/50 backdrop-blur-sm dark:border-macchiato-surface1/80 dark:bg-macchiato-mantle/50"
        onclick={() => history.back()}
      >
        <ArrowLeft class="size-4" strokeWidth={2} />
        Go back
      </Button>
    </div>

    {#if timestamp}
      <p class="not-found__meta mt-10 text-[10px] tracking-wide text-latte-text/35 dark:text-macchiato-text/35">
        {timestamp}
      </p>
    {/if}
  </div>
</section>

<style>
  .not-found__glow {
    animation: not-found-breathe 7s ease-in-out infinite;
  }

  .not-found__glow-2 {
    animation: not-found-drift 11s ease-in-out infinite;
  }

  .not-found__glow-3 {
    animation: not-found-drift 13s ease-in-out infinite reverse;
  }

  .not-found__stack {
    animation: not-found-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .not-found__numeral {
    animation: not-found-float 5.5s ease-in-out infinite;
  }

  .not-found__bookmark {
    animation: not-found-wiggle 4.2s ease-in-out infinite;
    transform-origin: 50% 0%;
  }

  .not-found__title,
  .not-found__copy,
  .not-found__actions {
    animation: not-found-rise 0.75s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .not-found__title {
    animation-delay: 0.06s;
  }

  .not-found__copy {
    animation-delay: 0.12s;
  }

  .not-found__actions {
    animation-delay: 0.18s;
  }

  @keyframes not-found-rise {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes not-found-float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes not-found-breathe {
    0%,
    100% {
      opacity: 0.85;
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.08);
    }
  }

  @keyframes not-found-drift {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(12px, -10px);
    }
  }

  @keyframes not-found-wiggle {
    0%,
    100% {
      transform: rotate(-6deg);
    }
    50% {
      transform: rotate(8deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .not-found__glow,
    .not-found__glow-2,
    .not-found__glow-3,
    .not-found__stack,
    .not-found__numeral,
    .not-found__bookmark,
    .not-found__title,
    .not-found__copy,
    .not-found__actions {
      animation: none;
    }
  }
</style>
